// Form types enum
export const ViewType = Object.freeze({
    SUBMISSIONS: "submissions",
    SUBMISSIONS_UNDER_REVIEW: "submissions_under_review",
    USERS: "users",
    REVIEWS: "reviews",
  });
  
  // Column types enum
  export const ColumnType = Object.freeze({
    Active: "Active",
    Inactive: "Inactive",
    Pending: "Pending",
  });

  //Types of columns defined in reports
  export const ReportColumnSourceType = Object.freeze({
    SUBMISSION: "Submission",
    DERRIVED: "Derrived",
    REVIEW: "Review"
  });

  //Render types for datagrid, detail panel and reports
  export const RenderContext = Object.freeze({
    DATAGRID: 'datagrid',
    DETAIL_PANEL: 'detailPanel',
    META_PANEL: 'metaPanel',
  });

  export const PermissionType = Object.freeze({
    SUPER_ADMIN: "SuperAdmin",
    ADMIN: "Admin",
    REVIEWERS: "Reviewers",
    INDIVIDUAL_USER: "IndividualUser"
  });

  export const ViewAccess = Object.freeze({
    SYSTEM:      "SYSTEM",      // platform built-in — always immutable, set by platform
    NONE:        "NONE",        // explicitly locked by owner/admin — nobody can edit
    OWNER:       "OWNER",       // only the view owner can edit
    ADMIN:       "ADMIN",       // all admins can edit
    SUPER_ADMIN: "SUPERADMIN",  // super admins only
  });

  export const ReportType = Object.freeze({
    ALL_VIEW: "AllView",
    VISIBLE_VIEW: "VisibleView",
    TEMPLATE: "Template",
    SINGLE_TEMPLATE: "SingleTemplate",
    FILTER: "Filter",
    BESPOKE_COLUMNS: "BespokeColumns",
    BESPOKE: "Bespoke"
  });

  export const ReportFormat = Object.freeze({
    EXCEL: "Excel",
    CSV: "Csv",
    PDF: "Pdf",
    DOC: "Doc",
    DOCX: "Docx",
    HTML: "Html",
    ZIP: "Zip"
  });

  export const ReportDataSource = Object.freeze({
    SUBMISSION: "SUBMISSION",
    REVIEW: "REVIEW",
    SUBMISSION_AND_REVIEW: "SUBMISSION_AND_REVIEW"
  });

  export const FilterConfig = Object.freeze({
    ALL: "All",
    SUBMITTED: "Submitted",
    COMPLETE: "Complete",
    INCOMPLETE: "Incomplete",
    REVIEWERS: "Reviewers",
    OTHERS: "Others"
  });

  export const AiComparisonStatus = Object.freeze({
    SAME: "same",
    SUGGESTION: "suggestion",
    NO_AI_PICK: "noAiPick",
    AMBIGUOUS: "ambiguous",
    DIFFERENT: "different",
    DISABLED: "disabled",
  });

  export const AiComparisonTone = Object.freeze({
    QUIET: "quiet",
    HELPFUL: "helpful",
    ATTENTION: "attention",
    NORMAL: "normal",
    URGENT: "urgent",
  });

  export const DEFAULT_AI_COMPARISON_TONE = Object.freeze({
    [AiComparisonStatus.SAME]: AiComparisonTone.QUIET,
    [AiComparisonStatus.SUGGESTION]: AiComparisonTone.HELPFUL,
    [AiComparisonStatus.NO_AI_PICK]: AiComparisonTone.QUIET,
    [AiComparisonStatus.AMBIGUOUS]: AiComparisonTone.ATTENTION,
    [AiComparisonStatus.DIFFERENT]: AiComparisonTone.NORMAL,
    [AiComparisonStatus.DISABLED]: AiComparisonTone.QUIET,
  });

  export const EventsSourceType = {
    USER: "USER",
    LAMBDA: "LAMBDA",
    SCHEDULER: "SCHEDULER",
    SYSTEM: "SYSTEM",
    AI: "AI",
    INTEGRATION: "INTEGRATION",
    QUEUE: "QUEUE",
    WORKFLOW: "WORKFLOW",
    ADMIN: "ADMIN",
  };
  
  export const ActionType = {
    INSERT: "INSERT",
    UPDATE: "UPDATE",
    DELETE: "DELETE",
    // add more as needed
  };  
  
  export const CategoryType = {
    LOGIN: "LOGIN",
    LOGOUT: "LOGOUT",
    CREATED: "CREATED",
    UPDATED: "UPDATED",
    DELETED: "DELETED",
    CLONED: "CLONED",
    UNKNOWN: "UNKNOWN",
    INITIALISE: "INITIALISE",
    COMMENT_ADDED: "COMMENT_ADDED",
    FILE_UPLOADED:  "FILE_UPLOADED",
    PERMISSIONS_UPDATED: "PERMISSIONS_UPDATED",
    REVIEW_ASSIGNED: "REVIEW_ASSIGNED",
    AI_REVIEW_GENERATED: "AI_REVIEW_GENERATED",
    AI_SUGGESTION: "AI_SUGGESTION",
    AI_ERROR: "AI_ERROR",
  }; 


// ---- Central stat-widget templates keyed by view type ----
export const STAT_WIDGETS_BY_TYPE = Object.freeze({
  [ViewType.SUBMISSIONS]: [
    { name: "Abstracts", value: 0 },
    { name: "Complete", value: 0 },
    { name: "Incomplete", value: 0 },
    { name: "Submitted", value: 0 },
  ],
  [ViewType.SUBMISSIONS_UNDER_REVIEW]: [
    { name: "Abstracts", value: 0 },
    { name: "Complete", value: 0 },
    { name: "Incomplete", value: 0 },
    { name: "Submitted", value: 0 },
  ],
  [ViewType.REVIEWS]: [
    { name: "Abstracts", value: 0 },
    { name: "Complete", value: 0 },
    { name: "Incomplete", value: 0 },
    { name: "Submitted", value: 0 },
  ],
  [ViewType.USERS]: [
    { name: "Users", value: 0 },
    { name: "Reviewers", value: 0 },
    { name: "Others", value: 0 },
  ],
});

// ---- Hard-coded pool options by view type (fill out as you decide) ----
export const DEFAULT_POOL_BY_TYPE = Object.freeze({
  [ViewType.SUBMISSIONS]: [
    // example stubs:
    { field: "trackingNumber", dataSource: "meta.trackingNumber", editable: false, headerName: "#", fieldType: "trackingNumber", visible: true, width: 80, origin: "hardcoded", linked: "submission" },
    { field: "status", dataSource: "data.status", headerName: "Status", fieldType: "select", visible: true, width: 150, editable: true, origin: "hardcoded", linked: "submission" },
    { field: "progress", dataSource: "meta.percentComplete", headerName: "Progress", fieldType: "progress", visible: true, width: 200, editable: false, origin: "hardcoded", linked: "submission" },
    { field: "complete", dataSource: "meta.complete", headerName: "Submitted", fieldType: "select", renderType: "BooleanSelect", visible: true, width: 140, editable: false, origin: "hardcoded", linked: "submission" },
    { field: "AIAssigned", dataSource: "meta.AIAssigned", headerName: "AI Assigned", fieldType: "select", renderType: "AIAssignedBadge", visible: true, width: 120, editable: false, origin: "hardcoded", linked: "submission" },
    { field: "id", dataSource: "id", headerName: "Id", fieldType: "string", visible: true, width: 140, editable: false, origin: "hardcoded", linked:"submission" },
    { field: "first_name", dataSource:"meta.owner" , headerName:"First name" , fieldType:"owner" , visible:true , width : 150 , editable:false , origin: "hardcoded" , linked:"submission"},
    { field: "last_name" , dataSource:"meta.owner" , headerName:"Last name" , fieldType:"owner" , visible:true , width : 150 , editable:false , origin: "hardcoded" , linked:"submission"},
    { field: "email", dataSource: "meta.owner", headerName: "Email", fieldType: "owner", visible: true, width: 225, editable: false, origin: "hardcoded", linked: "submission" },
    { field: "submitted_date", datasource:"createdAt", headerName: "Submitted date", fieldType: "dateTime", visible: true, width: 140, editable: true, origin: "hardcoded", linked: "submission" },
    { field: "payment",         headerName: "Submission Fee Paid", fieldType: "select", renderType: "BooleanSelect", visible: true, width: 140, editable: true, origin: "hardcoded", linked: "submission" },
    { field: "consent",         headerName: "All authors consent", fieldType: "select", renderType: "BooleanSelect", visible: true, width: 140, editable: true, origin: "hardcoded", linked: "submission" },
    { field: "screened", dataSource: "data.screened", headerName: "Screened", fieldType: "select", renderType: "BooleanSelect", visible: true, width: 100, editable: true, origin: "hardcoded", linked: "submission" },
    { field: "notes", dataSource: "data.notes", headerName: "Notes", fieldType: "multiline", visible: true, width: 140, editable: true, origin: "hardcoded", linked: "submission" },
  ],
  [ViewType.SUBMISSIONS_UNDER_REVIEW]: [
    { field: "progress", headerName: "Progress2", fieldType: "progress", visible: true, width: 120, editable: false, origin: "hardcoded", linked: "submission" },
  ],
  [ViewType.REVIEWS]: [
    { field: "score", headerName: "Score", fieldType: "select", visible: true, width: 120, editable: true, origin: "hardcoded", linked: "review" },
    { field: "trackingNumber", dataSource: "meta.trackingNumber", editable: false, headerName: "#", fieldType: "trackingNumber", visible: true, width: 80, origin: "hardcoded", linked: "review" },
    { field: "status", dataSource: "data.status", headerName: "Status", fieldType: "select", visible: true, width: 150, editable: true, origin: "hardcoded", linked: "review" },
    { field: "progress", dataSource: "meta.percentComplete", headerName: "Progress", fieldType: "progress", visible: true, width: 200, editable: false, origin: "hardcoded", linked: "review" },
    { field: "complete", dataSource: "meta.complete", headerName: "Submitted", fieldType: "select", renderType: "BooleanSelect", visible: true, width: 140, editable: false, origin: "hardcoded", linked: "review" },
    { field: "id", dataSource: "id", headerName: "Id", fieldType: "string", visible: true, width: 140, editable: false, origin: "hardcoded", linked: "review" },
    { field: "first_name", dataSource: "meta.owner", headerName: "Reviewer First name", fieldType:"owner", visible: true, width: 150, editable: false, origin: "hardcoded", linked: "review"},
    { field: "last_name", dataSource: "meta.owner", headerName: "Reviewer Last name", fieldType:"owner", visible: true, width: 150, editable: false, origin: "hardcoded", linked: "review"},
    { field: "email", dataSource: "meta.owner", headerName: "Reviewer Email", fieldType: "owner", visible: true, width: 225, editable: false, origin: "hardcoded", linked: 'review' },
    { field: "app_first_name", dataSource: "submission.meta.owner", headerName: "Submitter First name", fieldType:"owner", visible: true, width: 150, editable: false, origin: "hardcoded", linked: "review"},
    { field: "app_last_name", dataSource: "submission.meta.owner", headerName: "Submitter Last name", fieldType:"owner", visible: true, width: 150, editable: false, origin: "hardcoded", linked: "review"},
    { field: "app_email", dataSource: "submission.meta.owner", headerName: "Submitter Email", fieldType: "owner", visible: true, width: 225, editable: false, origin: "hardcoded", linked: 'review' },
    { field: "submitted_date", datasource:"createdAt", headerName: "Submitted date", fieldType: "dateTime", visible: true, width: 140, editable: true, origin: "hardcoded", linked: "review" },
    { field: "screened", dataSource: "data.screened", headerName: "Screened", fieldType: "select", renderType: "BooleanSelect", visible: true, width: 100, editable: true, origin: "hardcoded", linked: "review" },
    { field: "notes", dataSource: "data.notes", headerName: "Notes", fieldType: "multiline", visible: true, width: 140, editable: true, origin: "hardcoded", linked: "review" },
  ],
  [ViewType.USERS]: [
    { field: "id", dataSource: "id", headerName: "Id", fieldType: "string", visible: true, width: 140, editable: false, origin: "hardcoded", linked: "users" },
    { field: "givenName", dataSource: "givenName", headerName: "First name", fieldType: "string", visible: true, width : 150, "align": "left", editable: false, origin: "hardcoded", linked: "users"},
    { field: "familyName", dataSource: "familyName", headerName: "Last name", fieldType: "string", visible: true, width : 150, "align": "left", editable: false, origin: "hardcoded", linked: "users"},
    { field: "email", dataSource:"email", headerName: "Email", fieldType: "owner", visible: true, width: 220, "align": "left", editable: false, origin: "hardcoded", linked: "users" },
    { field: "submitter", dataSource: "submitter", headerName: "Submitter", fieldType: "userRole", attributes: { userRole: "submitter" }, visible: true, width: 140, "align": "left", editable: false, origin: "hardcoded", linked: 'users' },
    { field: "reviewer", dataSource: "reviewer", headerName: "Reviewer", fieldType: "userRole", attributes: { userRole: "reviewer" }, visible: true, width: 140, "align": "left", editable: false, origin: "hardcoded", linked: 'users' },
    { field: "admin", dataSource: "admin", headerName: "Admin", fieldType: "userRole", attributes: { userRole: "admin" }, visible: true, width: 140, "align": "left", editable: false, origin: "hardcoded", linked: 'users' },
    { field: "language", dataSource: "language", headerName: "Language", fieldType: "string", visible: true, width: 140, "align": "left", editable: false, origin: "hardcoded", linked: "users" },
    { field: "createdAt", dataSource: "createdAt", headerName: "Created Date", fieldType: "dateTime", visible: true, width: 200, "align": "center", editable: false, origin: "hardcoded", linked: "users" },
    { field: "lastLogin", dataSource: "lastLogin", headerName: "Last Login", fieldType: "dateTime", visible: true, width: 200, "align": "center", editable: false, origin: "hardcoded", linked: "users" },
  ],
});



// Determine the current environment from the environment variable
const environment = process.env.REACT_APP_USER_BRANCH;

console.log(`Current Environment: ${environment}`);

// Define Lambda URLs dynamically based on the environment
export const LambdaURLs = Object.freeze({
    GET_USER_ADMIN_TOKENS:
        environment === 'beta'
            ? 'https://agssg76eisec2tcf23v6gbf7bq0jylun.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://3woai6xe35wq7tjapmhabjw3ly0xdvzi.lambda-url.eu-west-1.on.aws/'
            : environment === 'dev'
            ? 'https://ew7hkxezxvbod4tvhjbhficije0pormm.lambda-url.eu-west-1.on.aws/'
            : 'https://na2nfrohc26m5gwlhkoii74e4e0oufrh.lambda-url.eu-west-1.on.aws/', //prod version

    ADD_USER_TO_PROJECT:
        environment === 'beta'
            ? 'https://beta-na2nfrohc26m5gwlhkoii74e4e0oufrh.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-na2nfrohc26m5gwlhkoii74e4e0oufrh.lambda-url.eu-west-1.on.aws/'
            : 'https://na2nfrohc26m5gwlhkoii74e4e0oufrh.lambda-url.eu-west-1.on.aws/',

    CREATE_USER:
        environment === 'beta'
            ? 'https://beta-buigew6pss24kkxh2aofy4ukaa0uofqm.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-buigew6pss24kkxh2aofy4ukaa0uofqm.lambda-url.eu-west-1.on.aws/'
            : 'https://buigew6pss24kkxh2aofy4ukaa0uofqm.lambda-url.eu-west-1.on.aws/',

    ADD_TOKEN_TO_USER:
        environment === 'beta'
            ? 'https://beta-gvjtmy5z3r232lfxyb7rksouhy0ytdin.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-gvjtmy5z3r232lfxyb7rksouhy0ytdin.lambda-url.eu-west-1.on.aws/'
            : 'https://gvjtmy5z3r232lfxyb7rksouhy0ytdin.lambda-url.eu-west-1.on.aws/',

    CREATE_ADMIN_USER:
        environment === 'beta'
            ? 'https://beta-cxdfi43aui2ulnwfssc4niwrme0rwhgo.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-cxdfi43aui2ulnwfssc4niwrme0rwhgo.lambda-url.eu-west-1.on.aws/'
            : 'https://cxdfi43aui2ulnwfssc4niwrme0rwhgo.lambda-url.eu-west-1.on.aws/',

    PROCESS_ASSIGNMENTS:
        environment === 'beta'
            ? 'https://beta-ialj6qgrluwziti2hof362r4gi0ixahn.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-ialj6qgrluwziti2hof362r4gi0ixahn.lambda-url.eu-west-1.on.aws/'
            : 'https://ialj6qgrluwziti2hof362r4gi0ixahn.lambda-url.eu-west-1.on.aws/',

    HANDLE_ERROR:
        environment === 'beta'
            ? 'https://aoczhs24ybxmiy55m7fgkrrupa0icrbx.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-aoczhs24ybxmiy55m7fgkrrupa0icrbx.lambda-url.eu-west-1.on.aws/'
            : 'https://aoczhs24ybxmiy55m7fgkrrupa0icrbx.lambda-url.eu-west-1.on.aws/',

    GET_TRACKING_NUMBER:
        environment === 'beta'
            ? 'https://beta-u4q7f5sad56o5qxxhfm57xtpyy0hmbml.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-u4q7f5sad56o5qxxhfm57xtpyy0hmbml.lambda-url.eu-west-1.on.aws/'
            : 'https://u4q7f5sad56o5qxxhfm57xtpyy0hmbml.lambda-url.eu-west-1.on.aws/',

    GET_FILE:
        environment === 'beta'
            ? 'https://beta-33rnohto4aozas56zc3nkxpnzm0mmndu.lambda-url.eu-west-1.on.aws/'
            : environment === 'staging'
            ? 'https://staging-33rnohto4aozas56zc3nkxpnzm0mmndu.lambda-url.eu-west-1.on.aws/'
            : 'https://33rnohto4aozas56zc3nkxpnzm0mmndu.lambda-url.eu-west-1.on.aws/',
});

export const AppBaseURL =
  environment === "dev"
    ? "https://dev.d2ttivmxqk5qb1.amplifyapp.com"
    : environment === "staging"
    ? "https://staging.d2ttivmxqk5qb1.amplifyapp.com"
    : "https://submissions.hyperion.live"; // prod

export const BuildSubmissionUrl = (formToken) => `${AppBaseURL}/${formToken}`;
export const BuildReviewUrl = (formToken) => `${AppBaseURL}/${formToken}`;

export const AssetsBucket = (() => {
    const env = process.env.REACT_APP_USER_BRANCH;

    if (env === "dev") return "hyperion-assets-dev";
    if (env === "staging") return "hyperion-assets-staging";
    if (env === "prod") return "hyperion-assets-prod";
    return "hyperion-assets-prod"; // default/fallback
})();
